"use strict";

/** @OrganizationStore */

var _ = require("lodash");

/* custom logo */
var brandConfig = require("../../../common/tmpConfig/brand.js");
var DEFAULT_BRAND_STORE = brandConfig.defaultConfig;
var customLogoConfig = brandConfig.clientConfig;

function getBrandConfig(brandId) {
  return _.assign(
    {},
    DEFAULT_BRAND_STORE,
    _.get(customLogoConfig, "" + brandId)
  );
}

var Store = function(
  APP_DISPATCHER_ID,
  Flux,
  IdentityService,
  ResourceHelper,
  statusFilter,
  $rootScope,
  SiteStore
) {
  function getUserStatus(users) {
    users.forEach(function(user) {
      user.status = statusFilter(user);
    });
  }

  var OrganizationStore = Flux.Store.create({
    name: "OrganizationStore",
    events: {
      UsersLoaded: true,
      SitesLoaded: true,
      PlanLoaded: true,
      DeletedProject: true
    }
  });

  /**
   * Initial empty store state
   * @var {Object} $state
   */
  OrganizationStore.$state = {
    current: null, // This will be populated upon opening Organization settings panel
    projects: [],
    users: [],
    plan: null,
    dirty: true
  };

  /**
   * Intialize the store bindings and initial data.
   * @constructor
   */
  OrganizationStore.init = function() {};

  OrganizationStore.getBrandConfig = function() {
    var ASSETS_DOMAIN = $rootScope.CONST.assets_domain;

    var brandConfig = getBrandConfig(
      IdentityService.get("currentOrganization").id
    );

    return _.extend({}, brandConfig, {
      logoUrl: brandConfig.logoUrl.replace("{ASSETS_DOMAIN}", ASSETS_DOMAIN),
      smallLogoUrl: brandConfig.smallLogoUrl.replace(
        "{ASSETS_DOMAIN}",
        ASSETS_DOMAIN
      )
    });
  };

  OrganizationStore.loadInvitations = function() {
    if (IdentityService.get("currentOrganization")) {
      var InvitationResource = ResourceHelper.getResource("Invitation");
      var orgId = IdentityService.get("currentOrganization").id;
      var userId = IdentityService.get("user").id;

      return InvitationResource.findAll(
        {
          include: "projects",
          user: userId
        },
        {
          organizationId: orgId,
          bypassCache: true
        }
      ).then(function(invitations) {
        return invitations;
      });
    } else {
      console.warn("No Organization");
      return null;
    }
  };

  OrganizationStore.loadOrganization = function() {
    var OrganizationResource = ResourceHelper.getResource("Organization");

    var request = {
      params: {
        include:
          "users,projects.users,projects.sites.compoundCoordinateReferenceSystems,plans.features,roles"
      }
    };
    if (OrganizationStore.get("dirty")) {
      request["bypassCache"] = true;
      OrganizationStore.$set("dirty", false);
    }
    return OrganizationResource.find(
      IdentityService.get("currentOrganization").id,
      request
    )
      .then(function(org) {
        var plan = _.find(org.plans, function(plan) {
          // this case should not exist [Bad Data]
          return plan.organizationId === org.id;
        }); // assumes one plan per organization

        SiteStore.syncSubscribe();

        // Pre-filter the project users that don't belong to this org
        org.projects.forEach(function(project) {
          project.users = project.users.filter(user => {
            return user._pivot_planId === plan.id;
          });
        });

        OrganizationStore.$set("current", org);
        OrganizationStore.$set("users", org.users);
        OrganizationStore.$set("projects", org.projects);
        OrganizationStore.$set("plan", plan);

        getUserStatus(org.users);

        var updatedOrg = _.assign({}, org, {
          brand: getBrandConfig(org.id)
        });
        return updatedOrg;
      })
      .then(org => {
        return OrganizationStore.loadUserSites(
          IdentityService.getUser().id,
          org
        ).then(() => {
          return org;
        });
      });
  };

  OrganizationStore.updateProjectUser = function(projectId, userId, action) {
    let projects = OrganizationStore.get("projects");
    let project = _.find(projects, project => {
      return project.id === projectId;
    });

    if (action === "destroy") {
      _.remove(project.users, user => {
        return user.id === userId;
      });
      _.map(projects, targetProject => {
        if (targetProject.id === project.id) {
          return project;
        } else {
          return targetProject;
        }
      });
      OrganizationStore.$set("projects", projects);
    }
  };

  OrganizationStore.loadUserSites = function(id, organization) {
    var OrganizationUserResource = ResourceHelper.getResource(
      "OrganizationUser"
    );
    return OrganizationUserResource.get(id, {
      params: {
        include: "userSites,organizations.roles",
        deleted: true
      },
      organizationId: organization.id
    }).then(function(user) {
      OrganizationStore.$set("user", user);
      return user;
    });
  };

  OrganizationStore.clearStore = function() {
    OrganizationStore.$set("current", null);
    OrganizationStore.$set("projects", []);
    OrganizationStore.$set("users", []);
    OrganizationStore.$set("plan", null);
    OrganizationStore.$set("dirty", true);
  };

  /**** Auxiliary methods ****/
  OrganizationStore._ORGANIZATION_ROLES = {
    7: "Pilot",
    8: "Normal User",
    9: "Admin",
    10: "Admin"
  };

  OrganizationStore._PROJECT_ROLES = {
    4: "Pilot",
    5: "Full Access",
    6: "Admin"
  };

  OrganizationStore._getOrgPermissionLevel = function(roleId) {
    return OrganizationStore._ORGANIZATION_ROLES[roleId];
  };

  OrganizationStore._getProjectAccessLevel = function(roleId) {
    return OrganizationStore._PROJECT_ROLES[roleId];
  };

  // Stores Handlers //////////////////////////////////////////////////////////////////

  this._handleSiteUpdated = site => {
    const projects = OrganizationStore.get("projects");
    const project = _.find(projects, { id: site.projectId });

    if (!project) {
      return;
    }

    if (site.name) {
      project.name = site.name;
    }

    project.sites[0] = site;

    OrganizationStore.$set("projects", projects);
  };

  this._handleSiteDeleted = site => {
    let projects = OrganizationStore.get("projects");
    const projectToDelete = _.find(projects, "id", site.projectId);

    if (projectToDelete) {
      projects = _.reject(projects, { id: projectToDelete.id });

      OrganizationStore.$set("projects", projects);
      OrganizationStore.$set("dirty", true);
      OrganizationStore.emit(
        OrganizationStore.Events.DeletedProject,
        projectToDelete
      );
    }
  };

  // Store Bindings
  SiteStore.on(
    SiteStore.Events.ChangedWithUpdatedSite,
    this._handleSiteUpdated.bind(this)
  );
  SiteStore.on(
    SiteStore.Events.ChangedWithDeletedSite,
    this._handleSiteDeleted.bind(this)
  );

  return OrganizationStore;
};

Store.$inject = [
  "APP_DISPATCHER_ID",
  "sbFlux",
  "IdentityService",
  "ResourceHelper",
  "statusFilter",
  "$rootScope",
  "SiteStore"
];
module.exports = Store;



// WEBPACK FOOTER //
// ./src/app/settings/dataStores/OrganizationStore.js